import { StyleProps } from "grapesjs";
import { INamedObject, IQuaternion, IVector3, UIVersions } from "./Misc";
import { GjsAttributeNames, GjsComponentTypes } from "../components/visageUI/gjsplugin/Enums";

export enum GrapesDeviceTypes {
	DesktopHD = "DesktopHD",
	Desktop4K = "Desktop4K",
	Tablet = "Tablet",
	Phone = "Phone",
	HoloLens2 = "HoloLens2",
}

export interface IDisplayDeviceInfo extends INamedObject
{
	id: string,
	name: string;
	aspect: string;
	resolution: {
		width: number;
		height: number;
	}
}

export const VisageUIDisplayDevices = new Map<GrapesDeviceTypes, IDisplayDeviceInfo>([
	[GrapesDeviceTypes.DesktopHD, {
		id: GrapesDeviceTypes.DesktopHD,
		name: 'Desktop HD',
		aspect: "16:9",
		resolution: {
			width: 1920,
			height: 1080,
		}
	}],
	[GrapesDeviceTypes.Desktop4K, {
		id: GrapesDeviceTypes.Desktop4K,
		name: 'Desktop 4K',
		aspect: "16:9",
		resolution: {
			width: 3840,
			height: 2160,
		}
	}],
	[GrapesDeviceTypes.Phone, {
		id: GrapesDeviceTypes.Phone,
		name: 'Phone',
		aspect: "19.5:9",
		resolution: {
			width: 2532,
			height: 1170,
		}
	}],
	[GrapesDeviceTypes.Tablet, {
		id: GrapesDeviceTypes.Tablet,
		name: 'Tablet',
		aspect: "4:3",
		resolution: {
			width: 2160,
			height: 1620,
		}
	}],
	[GrapesDeviceTypes.HoloLens2, {
		id: GrapesDeviceTypes.HoloLens2,
		name: 'HoloLens 2',
		aspect: "3:2",
		resolution: {
			width: 900,
			height: 600,
		}
	}],
]);

export const defaultDevice = () => VisageUIDisplayDevices.get(GrapesDeviceTypes.Phone) ?? {
	id: GrapesDeviceTypes.Phone,
	name: 'Phone',
	aspect: "19.5:9",
	resolution: {
		width: 2532,
		height: 1170,
	}
};

export interface IVisageSaveData
{
	stepUIData: Record<string, IGrapesJSProjectData>;
	stepTemplateMap: Record<string, string>;
	uiTemplateData: Record<string, IVisageTemplate>;
	cameraCanvasPairings: Record<string, string[]>;
}

export interface IWebViewData
{
	id: string;
	width: number;
	height: number;
	cutouts: IWebViewCutout[];
}

export interface IWebViewCutout
{
	divId: string;
	alphaDivId: string;
	isWorldspace: boolean;
	scale: IVector3;
	position: IVector3;
	rotation?: IQuaternion;
}

export enum GrapesRenderModes {
	Preview,
	Publish,
}

export enum VisageTemplateFormatVersions {
	latest = "1.0", // IMPORTANT: UPDATE THIS WHEN CHANGING THE TEMPLATE FORMAT AND ADD THE OLD VALUE TO THE ENUM BELOW!
}

// see https://scopearcloud.atlassian.net/wiki/spaces/AT/pages/2371354625/UI+Editor+2.0
export interface IVisagePublishCutoutData
{
	divId: string; 							// html div ID of the cutout content (string)
	alphaDivId?: string; 					// html div ID of a R channel alpha map for the cutout (string, optional)
	isWorldspace: boolean; 					// if true, displays in worldspace.  if false, screen space on mobile or slate on HL2.  only 1 non worldspace allowed
	scale: IVector3; 						// vector3 - used for worldspace only
	position: IVector3; 					// vector3 - used for worldspace only
	rotation: IQuaternion; 					// quaternion - used for worldspace only
}

// see https://scopearcloud.atlassian.net/wiki/spaces/AT/pages/2371354625/UI+Editor+2.0
export interface IVisagePublishWebviewData
{
	id: string;								// asset id that points to a directory with an index.html in it to display (string)
	width?: number; 						// pixel width of host webview (integer)
	height?: number;						// pixel height of host webview (integer)
	fullScreen: boolean; 					// bool - if true, renders the full webView in screenSpace (or slate space) using the device size (width and height ignored)
	cutouts: IVisagePublishCutoutData[];	// array of web content cutouts
}

export interface IVisageTemplate extends INamedObject
{
	id: string;
	name: string;
	sourceTemplateId: string;
	content: IGrapesJSProjectData;
	isDefault: boolean;
	isTemporary?: boolean;
	previousState?: IGrapesJSProjectData;
	defaultTemplateID: string;
	version?: UIVersions;
	lastUsed?: number;
}

export interface IVisageEditorSettings
{
	fitCanvas: boolean;
	canvasScale: number;
	showViewport: boolean;
	automaticMode: boolean;
}

export type VisageContextStylesMap = { [componentId: string]: { [key in CssStates]: { [propName: string]: string } }}; // componentId -> state -> style -> value

export interface IVisageEditorContext
{
	projectId: string;
	projectData: IGrapesJSProjectData;
	components: IGrapesJSComponent[];
	componentStyles: VisageContextStylesMap;
}

export interface IVisageStepUIData
{
	[stepId: string]: IGrapesJSProjectData;
}

export interface IGrapesJSComponent
{
	type: string;
	attributes: {
		id: string;
		[key: string]: any;
	};
	resizable?: any;
	draggable?: boolean;
	selectable?: boolean;
	stylable?: boolean;
	highlightable?: boolean;
	components?: IGrapesJSComponent[];
	content?: string;
	tagName?: string;
	name?: string;
	"custom-name"?: string;
	"__prev-display"?: string;
	classes?: string[];
}

export interface IGrapesJSFrameComponent
{
	components: IGrapesJSComponent[];
	stylable: string[];
	type: string;
	attributes?: {
		id: string;
		[key: string]: any;
	};
	"custom-name"?: string;
}

export interface IGrapesJSFrame
{
	id: string;
	component: IGrapesJSFrameComponent;
}

export interface IGrapesJSPage
{
	id: string;
	type: string;
	frames: IGrapesJSFrame[];
}

export interface IGrapesJSStyle
{
	selectors: Array<string | { [key: string]: string | boolean}>;
	selectorsAdd?: string;
	style: { [key: string]: string };
	mediaText?: string;
	state?: CssStates;
}

export interface IGrapesJSProjectData
{
	assets: any[];
	pages: IGrapesJSPage[];
	styles: IGrapesJSStyle[];
}

export enum VisageEditorWindowModes {
	Template,
	Step,
}

export interface IGrapesClipboardComponent {
	structure: string;
	style: Record<string, StyleProps>;
}

export interface ICssNumericValue {
	value: string;
	unit: CssUnits;
}

export enum CssUnits {
	px = "px",
	em = "em",
	rem = "rem",
	vh = "vh",
	vw = "vw",
	percent = "%",
}

export enum CssStates {
	Normal = "",
	Hover = "hover",
	Active = "active",
}

export enum VisageAddedCssPropertyNames {
	MinWidth = "Min-Width",
	MinHeight = "Min-Height",
	MaxWidth = "Max-Width",
	MaxHeight = "Max-Height",
	Flex = "Flex",
	Margin = "Margin",
	AbsolutePosition = "Absolute-Position",
	Scrolling = "Scrolling",
	Stroke = "Stroke",
	Shadow = "Shadow",
	Scale = "Scale",
}

export enum VisageAddedAttributeNames {
	SmartIdentifier = "Smart-Identifier",
}

export enum CssProperties {
	TransformOrigin = "transform-origin",
	Transform = "transform",
	Display = "display",
	Position = "position",
	Left = "left",
	Top = "top",
	Right = "right",
	Bottom = "bottom",
	Width = "width",
	Height = "height",
	MinWidth = "min-width",
	MinHeight = "min-height",
	MaxWidth = "max-width",
	MaxHeight = "max-height",
	FlexWrap = "flex-wrap",
	FlexDirection = "flex-direction",
	FlexJustify = "justify-content",
	FlexAlign = "align-items",
	Margin = "margin",
	MarginLeft = "margin-left",
	MarginTop = "margin-top",
	MarginRight = "margin-right",
	MarginBottom = "margin-bottom",
	Padding = "padding",
	PaddingLeft = "padding-left",
	PaddingTop = "padding-top",
	PaddingRight = "padding-right",
	PaddingBottom = "padding-bottom",
	OverflowX = "overflow-x",
	OverflowY = "overflow-y",
	Background = "background",
	BackgroundColor = "background-color",
	BackgroundImage = "background-image",
	BorderRadius = "border-radius",
	BorderRadiusTopLeft = "border-top-left-radius",
	BorderRadiusTopRight = "border-top-right-radius",
	BorderRadiusBottomRight = "border-bottom-right-radius",
	BorderRadiusBottomLeft = "border-bottom-left-radius",
	BorderColor = "border-color",
	BorderWidth = "border-width",
	BorderStyle = "border-style",
	BoxShadow = "box-shadow",
	FontFamily = "font-family",
	FontSize = "font-size",
	FontWeight = "font-weight",
	FontStyle = "font-style",
	LineHeight = "line-height",
	LetterSpacing = "letter-spacing",
	TextAlign = "text-align",
	Color = "color",
	ZIndex = "z-index",
}

export enum CssBorderStyles {
	None = "none",
	Solid = "solid",
	Dotted = "dotted",
	Dashed = "dashed",
	Double = "double",
	Groove = "groove",
	Ridge = "ridge",
	Inset = "inset",
	Outset = "outset",
	Hidden = "hidden",
}

export enum CssFontFamilies {
	Roboto = "Roboto",
	Arial = "Arial",
	Verdana = "Verdana",
	Tahoma = "Tahoma",
	TrebuchetMS = "Trebuchet MS",
	TimesNewRoman = "Times New Roman",
	Georgia = "Georgia",
	Garamond = "Garamond",
	CourierNew = "Courier New",
	BrushScriptMT = "Brush Script MT",
}

export enum CssFontWeights {
	Thin = "100",
	ExtraLight = "200",
	Light = "300",
	Normal = "400",
	Medium = "500",
	SemiBold = "600",
	Bold = "700",
	ExtraBold = "800",
	Black = "900",
	ExtraBlack = "950",
}

export enum CssFontStyles {
	Normal = "",
	Italic = "italic",
	//Oblique = "oblique",
}

export enum CssTextAlign {
	Left = "left",
	Right = "right",
	Center = "center",
	Justify = "justify",
}

export enum CssDisplayTypes {
	Block = "block",
	Inline = "inline",
	InlineBlock = "inline-block",
	Flex = "flex",
	InlineFlex = "inline-flex",
	Grid = "grid",
	InlineGrid = "inline-grid",
	Table = "table",
	TableRow = "table-row",
	TableCell = "table-cell",
	TableCaption = "table-caption",
	TableColumn = "table-column",
	TableColumnGroup = "table-column-group",
	TableFooterGroup = "table-footer-group",
	TableHeaderGroup = "table-header-group",
	TableRowGroup = "table-row-group",
	Contents = "contents",
	None = "none",
}

export enum CssPositionTypes {
	None = "",
	Static = "static",
	Relative = "relative",
	Absolute = "absolute",
	Fixed = "fixed",
	Sticky = "sticky",
}

export enum CssOverflowTypes {
	None = "",
	Auto = "auto",
	Hidden = "hidden",
}

export enum SmartIdentifierStates {
	None = "",
	Enabled = "enabled",
	Disabled = "disabled",
}

export interface IAssociatedProperty<T>
{
	/** the name of the real CSS property */
	name: T;
	/** the default value that will be set to the property when it's added to the component. can use the componentId when the value depends on the individual component (in case of multiple selected components) */
	defaultValue: (componentId: string) => string;
	/** the property should not render if its value isn't one of these */
	valueWhitelist?: string[];
	/** the property should not render if its value doesn't include this text */
	mustIncludeSubstring?: string;
}

export interface ICssPropertyRenderQuery {
	/** the name of the optional property drawer section, used for visual display purposes */
	propertyName: VisageAddedCssPropertyNames;
	associatedCssProperties: Array<IAssociatedProperty<CssProperties>>;
	typeWhitelist?: GjsComponentTypes[];
	/** the property should not render for components of these types */
	typeBlacklist?: GjsComponentTypes[];
	/** should we perform the query on the editor context (temporary storage) or the main store (permanent storage) */
	checkTemporary?: boolean;
}

export interface IAttributePropertyRenderQuery {
	attributeName: VisageAddedAttributeNames;
	associatedAttributes: Array<IAssociatedProperty<GjsAttributeNames>>;
	typeWhitelist?: GjsComponentTypes[];
	typeBlacklist?: GjsComponentTypes[];
}

export interface IBaseGrapesCssPropertyProps<T>
{
	name: string;
	value: T;
	label?: React.ReactNode;
	["data-name"]?: string;
	multivalues?: boolean;
	onChangeCallback?: (newValue: T) => void;
	onCommitCallback?: (newValue: T) => void;
}

export const embeddedImagesBase64 = {
	home: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNTQiIHZpZXdCb3g9IjAgMCA2MCA1NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0xMy41MDk4IDIxLjYwMzRMMjkuNzAzMSA1LjQxMDA2TDQ1Ljg5NjQgMjEuNjAzNEg0NS44OTI4VjQ4LjU4NjJIMTMuNTEzNVYyMS42MDM0SDEzLjUwOThaTTguMTE2OTEgMjYuOTk2M0w0LjEzMjA1IDMwLjk4MTFMMC4zMTg4NDggMjcuMTY3OUwyNS44OTA0IDEuNTk2MzZDMjcuOTk2MSAtMC41MDkzMzUgMzEuNDEwMSAtMC41MDkzMzUgMzMuNTE1OCAxLjU5NjM2TDU5LjA4NzQgMjcuMTY3OUw1NS4yNzQyIDMwLjk4MTFMNTEuMjg5NCAyNi45OTYzVjQ4LjU4NjJDNTEuMjg5NCA1MS41NjY2IDQ4Ljg3MzIgNTMuOTgyNyA0NS44OTI4IDUzLjk4MjdIMTMuNTEzNUMxMC41MzMgNTMuOTgyNyA4LjExNjkxIDUxLjU2NjYgOC4xMTY5MSA0OC41ODYyVjI2Ljk5NjNaIiBmaWxsPSIjRUJFQkVCIi8+Cjwvc3ZnPg==",
	next: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDEiIGhlaWdodD0iNzEiIHZpZXdCb3g9IjAgMCA0MSA3MSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0wLjMwNjUwMyA2NC4xNzlMNi4yMTk2OCA3MC4wODIzTDQwLjkyMTkgMzUuNDY2OUw2LjIxOTY4IDAuODUxNTYyTDAuMzA2NTAzIDYuNzU0ODZMMjcuOTY3MyAzNS40NjY5TDAuMzA2NTAzIDY0LjE3OVoiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo=",
	prev: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDEiIGhlaWdodD0iNzEiIHZpZXdCb3g9IjAgMCA0MSA3MSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik00MC42OTM1IDY0LjI0OTNMMzQuNzgwMyA3MC4xNTI2TDAuMDc4MTI1IDM1LjUzNzNMMzQuNzgwMyAwLjkyMTg3NUw0MC42OTM1IDYuODI1MTdMMTMuMDMyNyAzNS41MzczTDQwLjY5MzUgNjQuMjQ5M1oiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo=",
	scopeLogo: "data:image/webp;base64,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",
	scopeCube: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDMiIGhlaWdodD0iNTEiIHZpZXdCb3g9IjAgMCA0MyA1MSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0zOS4xODc2IDM2LjYyNTNMMjMuNTYwNCA0Ni4xNzc4QzIzLjQxMzUgNDYuMjY4IDIzLjIyNSA0Ni4xNjA2IDIzLjIyNDIgNDUuOTg3TDIzLjIwMDQgNDEuMzI0MkMyMy4yMDA0IDQxLjA0OTEgMjMuMzQyMiA0MC43ODE4IDIzLjU4MDggNDAuNjQyNkwzNC44MDk4IDM0LjAzM0MzNS4wNTYgMzMuODg2OSAzNS4yMDggMzMuNjIzIDM1LjIwOCAzMy4zMzY4TDM1LjE4MjUgMjUuNjk4NEMzNS4xODI1IDI1LjQwNTMgMzUuMDMwNSAyNS4xNDgzIDM0Ljc3NjcgMjUuMDAyMkMzNC41MzA0IDI0Ljg1NTIgMzQuMjM0MSAyNC44NTUyIDMzLjk3NjggMjUuMDA5MUwzMi4zNjg3IDI1Ljk2NTdDMzIuMTMzNSAyNi4xMDg0IDMxLjk3ODEgMjYuMzc2NSAzMS45NzgxIDI2LjY2MTlMMzEuOTk2IDMxLjgxNTVMMzEuODk0OSAzMi4wMDI4TDIzLjQ5NzYgMzYuOTUxQzIzLjQwMzMgMzcuMDEzOCAyMy4zMTY3IDM2Ljk2OTEgMjMuMjg0NSAzNi45NTFDMjMuMjUxMyAzNi45MzMgMjMuMTgyNiAzNi44ODE0IDIzLjE4MjYgMzYuNzcxNEwyMy4xNzU4IDI3LjIzNzhDMjMuMTc1OCAyNi45NDQ3IDIzLjIyNTkgMjYuNzY1IDIzLjQ5NzYgMjYuNTkyM0wzNS40NTc2IDE5LjM5NzRMMzUuNTY2MyAxOS4zMTMxTDM5LjEzNjcgMTcuMTg2N0MzOS4yMzA5IDE3LjEyODMgMzkuMzE0MSAxNy4xNjg3IDM5LjMzOTYgMTcuMTg2N0MzOS4zNzUzIDE3LjIwNTYgMzkuNDUxNyAxNy4yNTYzIDM5LjQ1MTcgMTcuMzY2NEwzOS41MjA0IDM2LjEwNzhDMzkuNTIwNCAzNi4yOTg3IDM5LjM4OTcgMzYuNTA0MSAzOS4xODc2IDM2LjYyNTNaTTIxLjgzOTMgMjMuNzI5OUMyMS42OTQxIDIzLjgwNzMgMjEuNDczNCAyMy44NjkyIDIxLjI1MjYgMjMuNzU1N0w1LjAzNDQ2IDE0LjI2NTlDNC45MzM0MiAxNC4yMDc1IDQuOTI1NzggMTQuMTE5OCA0LjkyNTc4IDE0LjA3NkM0LjkyNTc4IDE0LjA0MjUgNC45MzM0MiAxMy45NDcxIDUuMDI2ODIgMTMuODk2M0wyMS4xNjk0IDQuNjExMTJDMjEuMzUzNyA0LjUwODg0IDIxLjU0OTggNC41MTU3MSAyMS43NTYxIDQuNjMwMDNMMjkuOTA2NCA5LjUzNzhMMzcuMzY4IDE0LjAzNzNDMzcuNTQ0NyAxNC4xNDMgMzcuNTQzOCAxNC40MDI2IDM3LjM2NzIgMTQuNTA3NEwzNC4wNTI0IDE2LjQ3NjZDMzMuNjcyOSAxNi43IDMzLjIwMTYgMTYuNyAzMi44MTQ1IDE2LjQ4NzdMMjEuODcxNiAxMC4xNTMyQzIxLjYyNTQgMTAuMDA3MSAyMS4zMjE0IDEwLjAwNzEgMjEuMDY0MSAxMC4xNTMyTDE0LjQ3NzkgMTQuMjQwMUMxNC4yMzE2IDE0LjM4NzEgMTQuMDc5NiAxNC42NDQxIDE0LjA3OTYgMTQuOTM3MkMxNC4wNzk2IDE1LjIyMjYgMTQuMjMxNiAxNS40ODY0IDE0LjQ3NzkgMTUuNjMzNEwxNi4wNjczIDE2LjU2NDJDMTYuMzEzNiAxNi43MTEyIDE2LjYyMTggMTYuNzExMiAxNi44NzQ4IDE2LjU2NDJMMjEuMzc5MSAxMy43MjM2SDIxLjU5MzFMMjkuOTMxOSAxOC41NDM3QzMwLjAyNjEgMTguNjAyMSAzMC4wNDA1IDE4LjY5MDcgMzAuMDQwNSAxOC43MjMzQzMwLjA0MDUgMTguNzY3MSAzMC4wMzM3IDE4Ljg1MTQgMjkuOTM5NSAxOC45MTQxTDIxLjgzOTMgMjMuNzI5OVpNMTkuOTEyNyA0NS43MzA1QzE5Ljg4MDUgNDUuNzQ1MSAxOS43OTMgNDUuNzgxMiAxOS42OTg4IDQ1LjczMDVMMy42OTQ1OSAzNi4zMzk1QzMuNDIyODkgMzYuMTg1NyAzLjM5MDYyIDM2LjAxMjkgMy4zOTA2MiAzNS44OTI2TDMuMzUxNTcgMTcuNDgxMUMzLjM1MDcyIDE3LjI4NTIgMy41NTk1OSAxNy4xNjMxIDMuNzI2ODYgMTcuMjYxMUw3LjMxNTA0IDE5LjM1NzRDNy42NTU1MiAxOS41NTUxIDcuODU4NDUgMTkuOTE0NCA3Ljg1ODQ1IDIwLjMxMDZMNy45MTI3OSAzMy40NDA0QzcuOTEyNzkgMzMuNzI1OCA4LjA2NDc3IDMzLjk5MzkgOC4zMTEgMzQuMTRMMTUuMDUwMSAzOC4xMzE2QzE1LjMwNjUgMzguMjc4NiAxNS42MDM3IDM4LjI3ODYgMTUuODQ5OSAzOC4xMzE2QzE2LjA5NjIgMzcuOTc3NyAxNi4yNDgxIDM3LjcxNzMgMTYuMjQxNCAzNy40MjQyTDE2LjIyMjcgMzUuNTQwMkMxNi4yMjI3IDM1LjI1ODMgMTYuMDYzOSAzNC45OTEgMTUuODI0NSAzNC44NTE3TDExLjIxOTEgMzIuMDcyOUwxMS4xMDYyIDMxLjg5MzNMMTEuMDczOSAyMi4wMzMxQzExLjA3MzkgMjEuOTMwOCAxMS4xNDI3IDIxLjg2ODEgMTEuMTc0OSAyMS44NUMxMS4yMDggMjEuODM1NCAxMS4yOTQ2IDIxLjc5ODQgMTEuMzg4OSAyMS44NUwxOS42NTg5IDI2LjU5MjdDMTkuODYxOCAyNi43MTM5IDE5LjkxOTUgMjcuMDIxNiAxOS45MTk1IDI3LjA3MzJMMjAuMDEzOCA0NS41Mzk2QzIwLjAxMzggNDUuNjQ5NyAxOS45NDkzIDQ1LjcwMTIgMTkuOTEyNyA0NS43MzA1Wk00Mi42NzcyIDE0LjA4NTVDNDIuNjc2MyAxMy42NzM4IDQyLjQ2MjQgMTMuMjkzOSA0Mi4xMTI1IDEzLjA4NDJMMjEuODQ4NiAwLjkzNzcxMkMyMS42MDIzIDAuNzkxNTk2IDIxLjI5ODQgMC43OTE1OTYgMjEuMDUyMSAwLjkzNzcxMkwwLjcyNTMwNSAxMy4wMThDMC4zODkwNzIgMTMuMjE3NCAwLjE4Mjc0NyAxMy41ODI3IDAuMTgzNTk2IDEzLjk3NjRMMC4xOTIwODcgMzcuNTk5OEMwLjE5ODg4IDM3Ljg5MjkgMC4zNTUxMDkgMzguMTYwMiAwLjU5MDMwMyAzOC4yOTZDMC41OTAzMDMgMzguMjk2IDE3LjM0NDIgNDguMjM2MiAyMS4xMjA5IDUwLjQzOTFDMjEuNTgxMSA1MC43MDczIDIyLjE0MjMgNTAuNjk2MSAyMi41OTQ5IDUwLjQyMTFMNDIuMzI1NyAzOC41MzA3QzQyLjU3MTkgMzguMzgzNyA0Mi43MjczIDM4LjExNjQgNDIuNzI3MyAzNy44MjMzTDQyLjY3NzIgMTQuMDg1NVoiIGZpbGw9IiNFMUE0MkUiLz4KPC9zdmc+Cg==",
	workLinkLogo: "data:image/gif;base64,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",
	videoCoverPlaceholder: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxnIG9wYWNpdHk9IjAuMiI+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMjEwIDUwSDExMEwxMTAgMTMwSDIxMFY1MFpNMTEwIDQwQzEwNC40NzcgNDAgMTAwIDQ0LjQ3NzIgMTAwIDUwVjEzMEMxMDAgMTM1LjUyMyAxMDQuNDc3IDE0MCAxMTAgMTQwSDIxMEMyMTUuNTIzIDE0MCAyMjAgMTM1LjUyMyAyMjAgMTMwVjUwQzIyMCA0NC40NzcyIDIxNS41MjMgNDAgMjEwIDQwSDExMFoiIGZpbGw9IndoaXRlIi8+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTkwIDkwTDE0NSA2NUwxNDUgMTE1TDE5MCA5MFpNMTU1IDk4LjMzMzNMMTU1IDgxLjY2NjdMMTcwIDkwTDE1NSA5OC4zMzMzWiIgZmlsbD0id2hpdGUiLz4KPC9nPgo8L3N2Zz4K",
	audioCoverPlaceholder: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxnIG9wYWNpdHk9IjAuMiI+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNNzIuNSAyMEw0MS45NTg1IDMzLjg4MjVIMjVWNjMuODgyNUg0MS45NTg1TDcyLjUgNzcuNzY1VjIwWk00NSA1OS43NzI3TDY3LjUgNzBWMjcuNzY1TDQ1IDM3Ljk5MjNWNTkuNzcyN1pNMzAgMzguODgyNUg0MFY1OC44ODI1SDMwVjM4Ljg4MjVaIiBmaWxsPSJ3aGl0ZSIvPgo8L2c+Cjwvc3ZnPgo=",
	cameraCoverPlaceholder: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxnIG9wYWNpdHk9IjAuMiI+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTgwIDYwSDExMFYxMjBIMTgwVjYwWk0xMDAgNTBWMTMwSDE5MFY1MEgxMDBaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTE5MCA4MS45M1Y5OC4wN0wyMTAgMTA1LjU3Vjc0LjQzTDE5MCA4MS45M1pNMTgwIDc1VjEwNUwyMjAgMTIwVjYwTDE4MCA3NVoiIGZpbGw9IndoaXRlIi8+CjwvZz4KPC9zdmc+Cg==",
	imagePlaceholder: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxnIG9wYWNpdHk9IjAuMiI+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTUwLjAwMiA1MEg1MC4wMDE4VjE1MEgxNTAuMDAyVjUwWk00MC4wMDE4IDQwVjE2MEgxNjAuMDAyVjQwSDQwLjAwMThaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTc2LjY2ODcgODYuMjYyM0wxNDMuNTM4IDE1My4xMzFMMTM2LjQ2NiAxNjAuMjAyTDc2LjY2ODcgMTAwLjQwNEw0Ni44NzA5IDEzMC4yMDJMMzkuNzk5OCAxMjMuMTMxTDc2LjY2ODcgODYuMjYyM1oiIGZpbGw9IndoaXRlIi8+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTI2LjY2OCA4My4zMzM3QzEzMi4xOTEgODMuMzMzNyAxMzYuNjY4IDc4Ljg1NjYgMTM2LjY2OCA3My4zMzM3QzEzNi42NjggNjcuODEwOSAxMzIuMTkxIDYzLjMzMzcgMTI2LjY2OCA2My4zMzM3QzEyMS4xNDYgNjMuMzMzNyAxMTYuNjY4IDY3LjgxMDkgMTE2LjY2OCA3My4zMzM3QzExNi42NjggNzguODU2NiAxMjEuMTQ2IDgzLjMzMzcgMTI2LjY2OCA4My4zMzM3Wk0xMjYuNjY4IDkzLjMzMzdDMTM3LjcxNCA5My4zMzM3IDE0Ni42NjggODQuMzc5NCAxNDYuNjY4IDczLjMzMzdDMTQ2LjY2OCA2Mi4yODggMTM3LjcxNCA1My4zMzM3IDEyNi42NjggNTMuMzMzN0MxMTUuNjIzIDUzLjMzMzcgMTA2LjY2OCA2Mi4yODggMTA2LjY2OCA3My4zMzM3QzEwNi42NjggODQuMzc5NCAxMTUuNjIzIDkzLjMzMzcgMTI2LjY2OCA5My4zMzM3WiIgZmlsbD0id2hpdGUiLz4KPC9nPgo8L3N2Zz4K",
	prevLegacy: "data:image/png;base64,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",
	nextLegacy: "data:image/png;base64,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",
};

export enum VisageComponentTypes {
	Container = "container",
	Text = "text",
	Image = "image",
	Audio = "audio",
	Video = "video",
	Camera = "camera",
}

export enum VisageStyleStates {
	Normal = "normal",
	Hover = "hover",
	Active = "active",
}

export interface IVisageComponentStyle
{
	/** styling applied when the element is in its normal state without user interaction */
	[VisageStyleStates.Normal]?: Record<string, string>;
	/** styling applied when the element is hovered over */
	[VisageStyleStates.Hover]?: Record<string, string>;
	/** styling applied when the element is activated (usually when left-mouse-button is down on the element) */
	[VisageStyleStates.Active]?: Record<string, string>;
}

export interface IVisageComponentDefinition
{
	/** a name for display in user-friendly environment such as the editor GUI */
	displayName: string;
	type: VisageComponentTypes;
	/** a component can have any number of child components that together define a complete hierarchy */
	children?: Array<IVisageComponentDefinition>;
	/** a list of functions to invoke when the component is activated in the UI runtime */
	actions?: Array<string>,
	/** the text to display as the content of this element. note that this text will only show for "text" components */
	textContent?: string,
	/** translates to HTML attributes, e.g. onclick, src, href, etc */
	htmlAttributes?: Record<string, string>;
	/**
	 * a list of style objects that the component will "inherit" properties from  
	 * the component will copy the properties of each style object in the list in order, and then apply its own componentStyle properties on top of that  
	 * this will allow users to create a component that is a combination of multiple styles, and then customize it further  
	 */
	styleClassList?: Array<string>;
	/** the css properties of the component for each CSS state exposed to the user */
	style?: IVisageComponentStyle;
}

export interface IVisageComponentBlock
{
	/** used for organization and grouping in user-friendly environment such as the editor GUI */
	category: string;
	/** a name for display in user-friendly environment such as the editor GUI */		
	displayName: string;
	/** 
	 * a visual representation of the block in user-friendly environment such as the editor GUI  
	 * currently it's a base64 string of an SVG image
	 */
	displayIcon: string;
	/** 
	 * define the component hierarchy that the block generates when added to the editor  
	 * a block can only have a single hierarchy, with the component definition including its descendants structured as a tree
	 */
	rootComponent: IVisageComponentDefinition; 
}

export interface IVisageBlockDefinitions
{
	/** a list of style objects that can be used in component definitions as default reusable styles */
	styleClasses?: Record<string, IVisageComponentStyle>;
	/** a list of block definitions that the editor will use to populate the block library */
	blocks?: Record<string, IVisageComponentBlock>;
}

export const nativeHtmlEventAttributes = [
	"onabort",
	"onafterprint",
	"onbeforeprint",
	"onbeforeunload",
	"onblur",
	"oncanplay",
	"oncanplaythrough",
	"onchange",
	"onclick",
	"oncontextmenu",
	"oncopy",
	"oncuechange",
	"oncut",
	"ondblclick",
	"ondrag",
	"ondragend",
	"ondragenter",
	"ondragleave",
	"ondragover",
	"ondragstart",
	"ondrop",
	"ondurationchange",
	"onemptied",
	"onended",
	"onerror",
	"onfocus",
	"onformchange",
	"onforminput",
	"onhashchange",
	"oninput",
	"oninvalid",
	"onkeydown",
	"onkeypress",
	"onkeyup",
	"onload",
	"onloadeddata",
	"onloadedmetadata",
	"onloadstart",
	"onmessage",
	"onmousedown",
	"onmouseenter",
	"onmouseleave",
	"onmousemove",
	"onmouseout",
	"onmouseover",
	"onmouseup",
	"onmousewheel",
	"onoffline",
	"ononline",
	"onpagehide",
	"onpageshow",
	"onpaste",
	"onpause",
	"onplay",
	"onplaying",
	"onpopstate",
	"onprogress",
	"onratechange",
	"onreset",
	"onresize",
	"onscroll",
	"onseeked",
	"onseeking",
	"onselect",
	"onshow",
	"onstalled",
	"onstorage",
	"onsubmit",
	"onsuspend",
	"ontimeupdate",
	"onunload",
	"onvolumechange",
	"onwaiting",
	"onwheel"
];